<script setup lang="ts">
import { computed } from 'vue'

import BetaStatusBadge from '@/components/beta-dashboard/atoms/BetaStatusBadge.vue'
import type { BetaAiSuggestion } from '@/types/beta-dashboard'

const props = defineProps<{
    suggestion: BetaAiSuggestion
}>()

const emit = defineEmits<{
    action: [suggestion: BetaAiSuggestion]
}>()

const priorityLabel = computed(() => ({
    high: 'Prioridade alta',
    medium: 'Prioridade média',
    low: 'Melhoria sugerida',
}[props.suggestion.priority]))

const priorityTone = computed<'warning' | 'accent' | 'neutral'>(() => ({
    high: 'warning' as const,
    medium: 'accent' as const,
    low: 'neutral' as const,
}[props.suggestion.priority]))
</script>

<template>
    <article class="beta-suggestion-card">
        <div class="beta-suggestion-card__heading">
            <BetaStatusBadge :label="priorityLabel" :tone="priorityTone" />
            <span aria-hidden="true">✦</span>
        </div>

        <div class="beta-suggestion-card__copy">
            <h3>{{ props.suggestion.title }}</h3>
            <p>{{ props.suggestion.reason }}</p>
            <small v-if="props.suggestion.evidence">
                <strong>O que foi observado:</strong> {{ props.suggestion.evidence }}
            </small>
        </div>

        <button type="button" @click="emit('action', props.suggestion)">
            {{ props.suggestion.cta }}
            <span aria-hidden="true">→</span>
        </button>
    </article>
</template>

<style scoped>
.beta-suggestion-card {
    display: grid;
    min-height: 215px;
    align-content: space-between;
    gap: 18px;
    border: 1px solid #e0e7ef;
    border-radius: 20px;
    background: #fff;
    padding: 18px;
}

.beta-suggestion-card__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--main-color);
    font-size: 18px;
}

.beta-suggestion-card__heading :deep(.beta-status-badge) {
    font-size: 10px;
}

.beta-suggestion-card__copy h3 {
    color: #1a304e;
    font-size: 16px;
    font-weight: 850;
    letter-spacing: -0.015em;
}

.beta-suggestion-card__copy p {
    margin-top: 7px;
    color: #6f8098;
    font-size: 11px;
    line-height: 1.55;
}

.beta-suggestion-card__copy small {
    display: block;
    margin-top: 10px;
    color: #8a98aa;
    font-size: 11px;
    line-height: 1.5;
}

.beta-suggestion-card__copy small strong {
    color: #687b94;
}

.beta-suggestion-card > button {
    display: flex;
    min-height: 38px;
    align-items: center;
    justify-content: space-between;
    border-radius: var(--beta-button-radius);
    background: color-mix(in srgb, var(--main-color) 9%, white);
    padding: 0 12px;
    color: var(--main-color);
    font-size: 12px;
    font-weight: 850;
}
</style>
